import { z } from 'zod';
import { IdSchema } from '../common/index.js';

/**
 * Import de contacts d'un dossier (PARITE-5, démo brigitte/ImportContacts), ajout au contrat 1.0.0. Deux temps, jamais un seul :
 * l'aperçu (aucune écriture : nombre de lignes, dix premières, doublons, erreurs), puis la confirmation du MÊME fichier
 * (empreinte SHA-256) et du MÊME nombre de contacts à importer. Données personnelles : audience « staff » seulement.
 * Aucun envoi : l'import n'ajoute que des fiches ; les messages partent toujours par la messagerie, sous ses garde-fous.
 */

/** Limite du texte du fichier : le corps JSON de l'API est plafonné à 1 Mo (échappements compris). */
const TexteFichier = z.string().min(1).max(400_000);
const NomFichier = z.string().trim().min(1).max(200);
const Empreinte = z.string().regex(/^[0-9a-f]{64}$/);

export const ImportContactsRequestSchema = z
  .object({ csv: TexteFichier, fileName: NomFichier.optional() })
  .meta({ id: 'ImportContactsRequest', description: 'Texte du fichier CSV lu sur l’appareil (séparateur « ; » ou « , »).' });

export const ImportContactsConfirmSchema = ImportContactsRequestSchema.extend({
  fingerprint: Empreinte,
  expectedCount: z.number().int().min(1).max(2000),
}).meta({
  id: 'ImportContactsConfirm',
  description: 'Confirmation : le fichier de l’aperçu (empreinte) et le nombre de contacts annoncé ; sinon rien n’est écrit.',
});

export const ImportContactsOutcomeSchema = z.enum(['a_importer', 'doublon', 'erreur']).meta({ id: 'ImportContactsOutcome' });

export const ImportContactsRowSchema = z
  .object({
    /** Numéro de la ligne dans le fichier (l'en-tête est la ligne 1). */
    line: z.number().int().min(2),
    firstNames: z.string(),
    lastName: z.string(),
    vitalStatus: z.enum(['vivant', 'decede', 'inconnu']),
    /** Le mandant et l'auteur ne s'importent jamais : ils s'enregistrent depuis la fiche du dossier. */
    role: z.enum(['heritier', 'conjoint', 'autre']),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    preferredChannel: z.enum(['whatsapp', 'email', 'telephone', 'courrier']).nullable(),
    relationLabel: z.string().nullable(),
    outcome: ImportContactsOutcomeSchema,
    reason: z.string().nullable(),
  })
  .meta({ id: 'ImportContactsRow' });

export const ImportContactsIssueSchema = z
  .object({ line: z.number().int().min(2), reason: z.string().min(1) })
  .meta({ id: 'ImportContactsIssue' });

export const ImportContactsPreviewSchema = z
  .object({
    fingerprint: Empreinte,
    fileName: z.string().nullable(),
    separator: z.enum([';', ',']),
    totalRows: z.number().int().min(0),
    importableCount: z.number().int().min(0),
    duplicateCount: z.number().int().min(0),
    errorCount: z.number().int().min(0),
    preview: z.array(ImportContactsRowSchema).max(10),
    duplicates: z.array(ImportContactsIssueSchema),
    errors: z.array(ImportContactsIssueSchema),
    columnsRecognized: z.array(z.string()),
    columnsIgnored: z.array(z.string()),
    mandateSigned: z.boolean(),
    /** Jamais bloqué (décision de Joel du 30/10) : forçage du mandat par la dirigeante qui lève le blocage ; null sans forçage. */
    mandateForcing: IdSchema.nullable().optional(),
    limits: z.object({ maxRows: z.number().int().min(1), maxBytes: z.number().int().min(1), previewRows: z.number().int().min(1) }),
  })
  .meta({ id: 'ImportContactsPreview', description: 'Aperçu sans écriture : à importer, doublons écartés, erreurs à corriger.' });

export const ImportContactsReportSchema = z
  .object({
    createdCount: z.number().int().min(0),
    duplicateCount: z.number().int().min(0),
    errorCount: z.number().int().min(0),
    persons: z.array(z.object({ id: IdSchema, displayName: z.string().min(1) })),
  })
  .meta({ id: 'ImportContactsReport', description: 'Compte rendu de l’import : personnes ajoutées au dossier, rien d’envoyé.' });
